import {
  PencilIcon,
  ServerIcon as ServerIconOutline,
} from '@heroicons/react/outline';
import { ServerIcon } from '@heroicons/react/solid';

import {
  Button,
  Checkbox,
  Pagination,
  Paper,
  SkeletonLoader,
  Table,
  TextInput,
} from '@yoast/ui-library';
import type { ChangeEvent } from 'react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useGetAssociatedBrands } from '../../api/generated/insights';
import type { AssociatedBrandsResponseAssociatedBrandsItem } from '../../api/generated/insights.schemas';
import GradientText from '../../components/GradientText';
import PaperFooter from '../../components/PaperFooter';
import PaperHeader from '../../components/PaperHeader';
import { useBrandAnalysisData } from '../../contexts';
import EditGroupModal from './EditGroupModal';
import GroupBrandsModal from './GroupBrandsModal';

type Accessor = keyof AssociatedBrandsResponseAssociatedBrandsItem;

interface BrandListColumn {
  Header: string;
  accessor: Accessor;
  Cell?: ({
    value,
    row,
  }: {
    value: unknown;
    row: AssociatedBrandsResponseAssociatedBrandsItem;
  }) => React.ReactElement;
  LoadingComponent: ({ id }: { id: string }) => React.ReactElement;
}
type Brand = AssociatedBrandsResponseAssociatedBrandsItem;

/**
 * TODO:
 * - [x] Break Modal components into separate components
 * - [x] Connect Edit Group functionality
 * - [ ] Break Row into separate component
 * - [ ] No results found UI with option to clear the search filter
 * - [ ] Add Exclude/Include functionality (future version)
 * - [ ] Connect pagination (future version)
 */
const BrandList = () => {
  const navigate = useNavigate();

  const [hasUserMadeChanges, setHasUserMadeChanges] = useState(false);
  const [isBrandIncludedMap, setBrandIncludeMap] = useState<
    Record<string, boolean>
  >({});

  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [checkedBrands, setCheckedBrands] = useState<Record<string, boolean>>(
    {},
  );

  // Modal state
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [groupToEdit, setGroupToEdit] = useState<Brand | null>(null);

  const { brandId } = useBrandAnalysisData();

  function getBrandId(
    brand: AssociatedBrandsResponseAssociatedBrandsItem,
  ): string {
    return String(brand.id);
  }

  const { data: associatedBrandsData, isLoading: isLoadingAssociatedBrands } =
    useGetAssociatedBrands(brandId, {
      query: {
        enabled: !!brandId,
      },
    });

  const itemsPerPage = 10;

  // Filter brands based on search term
  const filteredBrands =
    associatedBrandsData?.data?.associatedBrands.filter((brand) => {
      if (!searchTerm) return true;
      return (brand.name?.toLowerCase() ?? '').includes(
        searchTerm.toLowerCase(),
      );
    }) || [];

  // Calculate pagination
  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;

  // Sort groups to the top, then paginate
  const sortedFilteredBrands = [...filteredBrands].sort(
    (a: Brand, b: Brand) => {
      if (a.type === 'group' && b.type !== 'group') return -1;
      if (a.type !== 'group' && b.type === 'group') return 1;
      return 0;
    },
  );

  const paginatedBrands = sortedFilteredBrands.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleEdit = (brandId: string) => {
    // Find the brand to edit
    const brandToEdit = filteredBrands.find(
      (brand) => getBrandId(brand) === brandId,
    );

    if (brandToEdit && brandToEdit.type === 'group') {
      setGroupToEdit(brandToEdit);
      setShowEditModal(true);
    } else {
      // TODO: Implement individual brand edit functionality
      //console.log('Individual brand edit not implemented yet');
    }
  };

  // get a list of the currently selected brands
  const selectedBrands = Object.entries(checkedBrands)
    .filter(([, isChecked]) => isChecked)
    .map(([id]) => id);

  const handleGroupBrands = () => {
    setShowGroupModal(true);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleCheckboxChange = (brandId: string) => {
    setCheckedBrands((prev) => ({
      ...prev,
      [brandId]: !prev[brandId],
    }));
  };

  const handleSaveChanges = () => {
    // make PUT call to modify brand list
    // on success, navigate
    navigate('/');
  };

  const handleResetChanges = () => {
    setCheckedBrands({});
    setBrandIncludeMap({});
    setHasUserMadeChanges(false);
  };

  // Modal handlers
  const handleCloseModal = () => {
    setShowGroupModal(false);
  };

  const handleGroupCreated = () => {
    // Clear selected brands after successful grouping
    setCheckedBrands({});
    // Reset pagination to page 1 to ensure we aren't on an invalid page
    setCurrentPage(1);
    // reset filter
    setSearchTerm('');
  };

  // Edit modal handlers
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setGroupToEdit(null);
  };

  const isBrandIncluded = (brandId: string, brand: Brand) => {
    // prefer explicit map value, otherwise infer from brand.isHidden
    if (brandId in isBrandIncludedMap) return !!isBrandIncludedMap[brandId];
    return !brand.isHidden;
  };

  const columns: BrandListColumn[] = [
    {
      Header: 'Brand name',
      accessor: 'name',
      Cell: ({ value, row: brand }: { value: unknown; row: Brand }) => (
        <span className="yst-font-medium yst-text-slate-900">
          <div className="yst-flex yst-items-center yst-gap-2">
            <Checkbox
              id={`brand-checkbox-${brand.id}`}
              checked={!!checkedBrands[getBrandId(brand)]}
              onChange={() => handleCheckboxChange(getBrandId(brand))}
              value={getBrandId(brand)}
              label={
                brand.isSelf ? (
                  // NOTE: this causes nasty react validation error/warning in console
                  // we should update the ui-library types to allow this
                  <GradientText>{value as string}</GradientText>
                ) : (
                  (value as string)
                )
              }
              name={`brand-checkbox-${brand.id}`}
              disabled={!isBrandIncluded(getBrandId(brand), brand)}
            />
            {brand.type === 'group' && (
              <ServerIcon className="yst-h-4 yst-w-4 yst-text-slate-400" />
            )}
          </div>
        </span>
      ),
      LoadingComponent: ({ id = '' }) => (
        <div className="yst-flex">
          <Checkbox
            id={`loading-row-checkbox-${id}`}
            value={'loading'}
            label=""
            disabled
            name={`brand-checkbox-${id}`}
          />{' '}
          <SkeletonLoader
            className="yst-h-6 yst-w-32"
            id={`loading-row-skeleton-${id}`}
          />
        </div>
      ),
    },
    // {
    //   Header: 'Mentions',
    //   accessor: 'mentionsFound',
    //   Cell: ({ row: brand }: { value: unknown; row: Brand }) => {
    //     return (
    //       <span className={classNames('yst-text-sm')}>
    //         <span
    //           className={classNames({
    //             'yst-text-slate-900': isBrandIncluded(getBrandId(brand), brand),
    //             'yst-text-slate-400': !isBrandIncluded(
    //               getBrandId(brand),
    //               brand,
    //             ),
    //           })}
    //         >
    //           {/* API doesn't expose mentions in generated schema; show placeholder */}
    //           {0}
    //         </span>{' '}
    //         <span
    //           className={classNames({
    //             'yst-text-slate-500': isBrandIncluded(getBrandId(brand), brand),
    //             'yst-text-slate-300': !isBrandIncluded(
    //               getBrandId(brand),
    //               brand,
    //             ),
    //           })}
    //         >
    //           / {/* placeholder total */ 0}
    //         </span>
    //       </span>
    //     );
    //   },
    //   LoadingComponent: () => <SkeletonLoader className="yst-h-6 yst-w-8" />,
    // },
    {
      Header: 'Actions',
      accessor: 'id',
      Cell: ({ value, row: brand }: { value: unknown; row: Brand }) => (
        <>
          {
            brand.type === 'group' ? (
              <Button
                variant="tertiary"
                size="small"
                // TODO: should open the group edit modal with this brand group's details
                // may need a new PUT method for updating an existing brand group
                onClick={() => handleEdit(String(value ?? ''))}
                className="yst-px-0"
                disabled={!isBrandIncluded(getBrandId(brand), brand)}
              >
                <PencilIcon className="yst-h-4 yst-w-4" />
                Edit group
              </Button>
            ) : undefined
            // (
            //   <Button
            //     variant="tertiary"
            //     size="small"
            //     onClick={() => handleEdit(String(value ?? ''))}
            //     className="yst-px-0"
            //     disabled={!isBrandIncluded(getBrandId(brand), brand)}
            //   >
            //     <PencilIcon className="yst-h-4 yst-w-4" />
            //     Rename
            //   </Button>
            // )
          }
        </>
      ),
      LoadingComponent: () => <SkeletonLoader className="yst-h-6 yst-w-24" />,
    },
    // {
    //   Header: 'Exclude',
    //   accessor: 'included',
    //   Cell: ({ row: brand }: { row: Brand }) => (
    //     <Button
    //       variant="tertiary"
    //       size="small"
    //       onClick={() => handleIncludedBrandToggle(brand)}
    //       className="yst-px-0"
    //     >
    //       {isBrandIncluded(getBrandId(brand), brand) ? (
    //         <>
    //           <EyeOffIcon className="yst-h-4 yst-w-4" /> Exclude
    //         </>
    //       ) : (
    //         <>
    //           <EyeIcon className="yst-h-4 yst-w-4" /> Include
    //         </>
    //       )}
    //     </Button>
    //   ),
    //   LoadingComponent: () => <SkeletonLoader className="yst-h-6 yst-w-20" />,
    // },
  ];

  const analysisTypeLabel = '';

  return (
    <Paper className="yst-mx-auto yst-max-w-2xl">
      <PaperHeader
        title={`Brand list ${analysisTypeLabel ? `in ${analysisTypeLabel}` : ''}`}
      >
        <p>
          These are the brands found in responses to your example prompts. Group
          related brands to improve your analysis. You can rename or exclude
          irrelevant brands, and new brands will be added automatically if they
          appear in future analysis runs.
        </p>
      </PaperHeader>
      <Paper.Content>
        {/* FILTER */}
        <div className="yst-mb-6">
          <TextInput
            id="brand-search"
            label="Search brands"
            placeholder="Search brands"
            value={searchTerm}
            onChange={handleSearchChange}
          />
        </div>

        {/* TABLE */}
        <div className="yst-mb-6">
          <Table columns={columns} className="yst-w-full" variant="minimal">
            <Table.Head>
              <Table.Row>
                {columns.map((column) => (
                  <Table.Header key={column.accessor}>
                    {column.Header}
                  </Table.Header>
                ))}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {/* LOADING ROWS */}
              {isLoadingAssociatedBrands ? (
                [1, 2, 3].map((_, idx) => (
                  <Table.Row key={idx}>
                    {columns.map((column, cellColIdx) => (
                      <Table.Cell key={`cell-${cellColIdx}`}>
                        <column.LoadingComponent
                          id={`skeleton-${idx}-${cellColIdx}`}
                          key={column.accessor}
                        />
                      </Table.Cell>
                    ))}
                  </Table.Row>
                ))
              ) : paginatedBrands.length === 0 ? (
                /* NO RESULTS FOUND */
                <Table.Row>
                  <Table.Cell colSpan={columns.length}>
                    <div className="yst-py-12 yst-text-center">
                      <h3 className="yst-mt-4 yst-text-lg yst-font-medium yst-text-slate-900">
                        No brands found
                      </h3>
                      <p className="yst-mt-2 yst-text-sm yst-text-slate-500">
                        {searchTerm
                          ? `No brands match "${searchTerm}". Try a different search term.`
                          : 'No brands have been found yet. Run an analysis to discover brands.'}
                      </p>
                      {searchTerm && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => {
                            setSearchTerm('');
                            setCurrentPage(1);
                          }}
                          className="yst-mt-4"
                        >
                          Clear search
                        </Button>
                      )}
                    </div>
                  </Table.Cell>
                </Table.Row>
              ) : (
                /* DATA ROWS */
                paginatedBrands
                  // .sort((a, b) => {
                  //   // Groups first
                  //   if (a.type === 'group' && b.type !== 'group') return -1;
                  //   if (a.type !== 'group' && b.type === 'group') return 1;
                  //   return 0;
                  // })
                  .map((brand) => (
                    <Table.Row key={brand.id}>
                      {columns.map((column) => (
                        <Table.Cell key={column.accessor}>
                          {column.Cell
                            ? column.Cell({
                                // pass a safe value to the Cell (may be undefined)
                                value: brand[column.accessor],
                                row: brand,
                              })
                            : brand[column.accessor]}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))
              )}
            </Table.Body>
          </Table>
        </div>

        <div className="yst-flex yst-items-center yst-justify-between">
          <Button
            variant="secondary"
            onClick={handleGroupBrands}
            className="yst-mb-0 yst-gap-2"
            disabled={selectedBrands.length < 2}
          >
            <ServerIconOutline /> Group brands
          </Button>

          <Pagination
            variant="text"
            current={currentPage}
            total={totalPages}
            onNavigate={setCurrentPage}
            className="yst-flex-row"
            screenReaderTextPrevious="Previous page"
            screenReaderTextNext="Next page"
          />
        </div>
      </Paper.Content>
      <PaperFooter>
        <div className="yst-flex yst-items-center yst-justify-end yst-gap-4">
          {hasUserMadeChanges ? (
            <>
              <Button
                variant="secondary"
                className="yst-mb-0"
                onClick={handleResetChanges}
              >
                Discard changes
              </Button>
              <Button
                variant="primary"
                className="yst-mb-0"
                onClick={handleSaveChanges}
              >
                Save changes
              </Button>
            </>
          ) : (
            // This was originally intended to be "Discard Changes" and
            // "Save changes" but the current implementation saves changes
            // immediately when brands are grouped or excluded, so this
            // provides a way back to the analysis and does not perform
            // any action
            <Button
              as={Link}
              to={`/brands/${brandId}`}
              variant="tertiary"
              className="yst-mb-0"
            >
              Return to analysis
            </Button>
          )}
        </div>
      </PaperFooter>

      {/* Group Brands Modal */}
      <GroupBrandsModal
        isOpen={showGroupModal}
        onClose={handleCloseModal}
        brandId={brandId}
        brandsToGroup={filteredBrands.filter((brand) =>
          selectedBrands.includes(getBrandId(brand)),
        )}
        onGroupCreated={handleGroupCreated}
      />

      {/* Edit Group Modal */}
      <EditGroupModal
        isOpen={showEditModal}
        onClose={handleCloseEditModal}
        groupToEdit={groupToEdit}
        allBrands={filteredBrands}
      />
    </Paper>
  );
};

export default BrandList;
